import _styled, { default as _styled2, default as _styled3, default as _styled4, default as _styled5, default as _styled6, default as _styled7 } from "styled-components";
import React from "react";
import { useFormContext, Controller } from "react-hook-form";
import { useRouter } from "next/router";
import { Input } from "~/features/ui/form/input";
import { Select } from "~/features/ui/form/select";
import { PhoneNumber, validatePhoneNumber } from "~/features/ui/form/phone-number";
import { TextArea } from "~/features/ui/form/textarea";
import { prefixes } from "~/features/blocks/inventory/marketo-form/options";
import { type MarketoSelectField, type MarketoFormField as MarketoFormFieldType } from "~/features/marketo/types";
import { MARKETO_UTM_FIELDS, MARKETO_UTM_MAP } from "~/features/marketo/utils";
import { getStoredUTMParams } from "~/features/ui/params/utils";
export type Variant = keyof typeof variants;
type Props = MarketoFormFieldType & {
  disabled?: boolean;
  variant: Variant;
};
const variants = {
  default: {
    general: {
      "gridColumn": "1 / -1",
      "@media (min-width: 1024px)": {
        "gridColumn": "span 6 / span 6"
      }
    },
    textarea: {
      "gridColumn": "1 / -1",
      "@media (min-width: 1024px)": {
        "gridColumn": "span 6 / span 6"
      }
    }
  },
  condensed: {
    general: {
      "gridColumn": "1 / -1",
      "@media (min-width: 1024px)": {
        "gridColumn": "span 4 / span 4"
      }
    },
    textarea: {
      "gridColumn": "1 / -1"
    }
  }
} as const;
export function MarketoFormField(field: Props) {
  const {
    id,
    dataType,
    label,
    required,
    validationMessage,
    disabled,
    visibilityRules,
    variant
  } = field;
  const router = useRouter();
  const {
    register,
    watch,
    setValue,
    formState: {
      errors
    },
    control
  } = useFormContext();
  const message = validationMessage || "Error";
  function isVisible() {
    if (!visibilityRules || !visibilityRules.rules) {
      return true;
    }
    return visibilityRules.rules.every(rule => {
      if (!rule.subjectField || !rule.values) {
        return true;
      }
      const fieldValue = watch(rule.subjectField);
      switch (rule.operator) {
        case "is":
          return rule.values.includes(fieldValue);
        // Implement other operators as needed
        default:
          return true;
      }
    });
  }
  React.useEffect(() => {
    if ("autoFill" in field) {
      let autofillValue;
      if (field.autoFill?.valueFrom === "query") {
        autofillValue = router.query[field.autoFill.parameterName] as string;
        if (MARKETO_UTM_FIELDS.includes(field.id)) {
          const storedUTMParams = getStoredUTMParams();
          const utmTag = MARKETO_UTM_MAP[field.id];
          if (storedUTMParams && utmTag) {
            autofillValue = storedUTMParams[utmTag];
          }
        }
      }
      autofillValue = autofillValue || field.autoFill?.value;
      if (autofillValue) {
        setValue(id!, autofillValue, {
          shouldDirty: true
        });
      }
    }
  }, [id, field, setValue, router.query]);
  const map = {
    text: () => <_StyledDiv $_css={[variants[variant].general, dataType === "hidden" && {
      "display": "none"
    }]}>
        <Input error={errors[id!]?.message as string} name={id} label={label} type="text" disabled={disabled} required={required} register={register(id!, {
        required: required ? {
          value: true,
          message
        } : false,
        validate: value => {
          // If the field is required, prevent all white space input
          if (!required) {
            return true;
          }
          return value.trim().length !== 0 || message;
        }
      })} />
      </_StyledDiv>,
    email: () => <_StyledDiv2 $_css2={[variants[variant].general, dataType === "hidden" && {
      "display": "none"
    }]}>
        <Input error={errors[id]?.message as string} name={id} label={label} type="email" disabled={disabled} required={required} register={register(id, {
        required: required ? {
          value: true,
          message
        } : false,
        pattern: {
          value: /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/,
          message
        },
        validate: value => {
          // If the field is required, prevent all white space input
          if (!required) {
            return true;
          }
          return value.trim().length !== 0 || message;
        }
      })} />
      </_StyledDiv2>,
    telephone: () => <_StyledDiv3 $_css3={[variants[variant].general, dataType === "hidden" && {
      "display": "none"
    }]}>
        <Controller name={id} control={control} defaultValue={prefixes[0]} render={({
        field
      }) => <PhoneNumber error={errors[id]?.message as string} value={field.value} required={required} onChange={field.onChange} label={label} prefixes={prefixes} />} rules={{
        required: required ? message : false,
        validate: value => required && !validatePhoneNumber(value) ? message : true
      }} />
      </_StyledDiv3>,
    select: () => {
      return <_StyledDiv4 $_css4={[variants[variant].general, dataType === "hidden" && {
        "display": "none"
      }]}>
          <Select error={errors[id]?.message as string} options={(field as MarketoSelectField).fieldMetaData?.values?.map(v => ({
          label: v.label || "",
          value: v.value || ""
        })) || []} defaultValue="" name={id} disabled={disabled} label={label} required={required} register={register(id, {
          required: required ? {
            value: true,
            message
          } : false
        })} />
        </_StyledDiv4>;
    },
    textArea: () => <_StyledDiv5 $_css5={[variants[variant].textarea, dataType === "hidden" && {
      "display": "none"
    }]}>
        <_StyledTextArea error={errors[id]?.message as string} name={id} label={label} disabled={disabled} required={required} register={register(id, {
        required: required ? {
          value: true,
          message
        } : false,
        validate: value => {
          // If the field is required, prevent all white space input
          if (!required) {
            return true;
          }
          return value.trim().length !== 0 || message;
        }
      })} $_css6={[variant === "condensed" && {
        "minHeight": "59px"
      }]} />
      </_StyledDiv5>,
    hidden: () => <_StyledDiv6 $_css7={[dataType === "hidden" && {
      "display": "none"
    }]}>
        <input type="hidden" {...register(id)} />
      </_StyledDiv6>
  } as const;
  const Component = map[dataType as keyof typeof map];
  if (!Component || !isVisible()) {
    return null;
  }
  return <>{Component()}</>;
}
var _StyledDiv = /*#__PURE__*/_styled("div").withConfig({
  componentId: "sc-sf-1dgo5ys-0"
})`${p => p.$_css}`;
var _StyledDiv2 = _styled2("div").withConfig({
  componentId: "sc-sf-1dgo5ys-1"
})`${p => p.$_css2}`;
var _StyledDiv3 = _styled3("div").withConfig({
  componentId: "sc-sf-1dgo5ys-2"
})`${p => p.$_css3}`;
var _StyledDiv4 = _styled4("div").withConfig({
  componentId: "sc-sf-1dgo5ys-3"
})`${p => p.$_css4}`;
var _StyledDiv5 = _styled5("div").withConfig({
  componentId: "sc-sf-1dgo5ys-4"
})`${p => p.$_css5}`;
var _StyledTextArea = _styled6(TextArea).withConfig({
  componentId: "sc-sf-1dgo5ys-5"
})`${p => p.$_css6}`;
var _StyledDiv6 = /*#__PURE__*/_styled7("div").withConfig({
  displayName: "marketo-form-field___StyledDiv6",
  componentId: "sc-uprkaf-0"
})(["", ""], p => p.$_css7);